/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { AnimationModalEnterName, animate, baseStyles, event, i18n, I18nService, state, property, reverseAnimation, focusTrap, ariaModal, firstFocus, layer, closable, triggerable, } from '@cds/core/internal';
import { html, LitElement } from 'lit';
import styles from './overlay.element.scss';
import sharedStyles from './shared.element.scss';
let CdsInternalStaticOverlay = class CdsInternalStaticOverlay extends LitElement {
    constructor() {
        super(...arguments);
        this.closable = false;
        // eslint-disable-next-line rulesdir/reserved-property-names
        this.hidden = false;
        this.demoMode = false;
        this.i18n = I18nService.keys.overlay;
    }
    // renderRoot needs delegatesFocus so that focus can cross the shadowDOM inside an element with aria-modal set to true
    static get shadowRootOptions() {
        return { ...super.shadowRootOptions, delegatesFocus: true };
    }
    get closeButtonTemplate() {
        return html `<cds-internal-close-button
      cds-layout="align:top"
      aria-label=${this.i18n.closeButtonAriaLabel}
      @click=${() => this.closeOverlay('close-button-click')}
    ></cds-internal-close-button>`;
    }
    get backdropTemplate() {
        return html `<div class="overlay-backdrop" aria-hidden="true" @click=${() => this.backdropClick()}></div>`;
    }
    render() {
        return html `
      ${this.backdropTemplate}
      <div class="private-host" tabindex="-1">
        <slot></slot>
      </div>
    `;
    }
    backdropClick() {
        if (this.layerController.isActiveLayer) {
            this.closeOverlay('backdrop-click');
        }
    }
    closeOverlay(trigger = 'custom') {
        this.closableController.close(trigger);
    }
    static get styles() {
        return [baseStyles, styles, sharedStyles];
    }
};
__decorate([
    property({ type: Boolean })
], CdsInternalStaticOverlay.prototype, "closable", void 0);
__decorate([
    property({ type: Boolean })
], CdsInternalStaticOverlay.prototype, "hidden", void 0);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsInternalStaticOverlay.prototype, "demoMode", void 0);
__decorate([
    i18n()
], CdsInternalStaticOverlay.prototype, "i18n", void 0);
CdsInternalStaticOverlay = __decorate([
    layer(),
    closable(),
    focusTrap(),
    ariaModal(),
    firstFocus(),
    triggerable()
], CdsInternalStaticOverlay);
export { CdsInternalStaticOverlay };
/**
 *
 * ```typescript
 * import '@cds/core/internal-components/overlay/register.js';
 * ```
 *
 * ```html
 * <cds-internal-overlay>
 *  <section cds-layout="vertical align:horizontal-stretch">
 *    <div cds-layout="vertical pad:md gap:md">
 *      <h2 cds-text="display">A Title</h2>
 *      <div>
 *        <p cds-text="body">
 *          Content inside a generic overlay.
 *        </p>
 *      </div>
 *    </div>
 *  </section>
 * </cds-internal-overlay>
 * ```
 *
 * @element cds-internal-overlay
 * @slot - Content slot for the content inside the overlay's panel
 * @event closeChange - Notify user when close event has occurred
 * @cssprop --backdrop-background
 * @cssprop --layered-backdrop-background
 * @cssprop --animation-duration
 * @cssprop --animation-easing
 *
 * KNOWN ISSUE: Safari jumps through the exit animation but only when the ESC key is pressed.
 *
 */
let CdsInternalOverlay = class CdsInternalOverlay extends CdsInternalStaticOverlay {
    constructor() {
        super(...arguments);
        this.cdsMotion = 'on';
    }
};
__decorate([
    property({ type: String })
], CdsInternalOverlay.prototype, "cdsMotion", void 0);
__decorate([
    event()
], CdsInternalOverlay.prototype, "cdsMotionChange", void 0);
CdsInternalOverlay = __decorate([
    animate({
        hidden: {
            true: reverseAnimation(AnimationModalEnterName),
            false: AnimationModalEnterName,
        },
    })
], CdsInternalOverlay);
export { CdsInternalOverlay };
